import React, {
	CSSProperties,
	ErrorInfo,
	ReactNode,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { MdxJsxFlowElement } from 'mdast-util-mdx-jsx'
import {
	Node,
	List,
	Heading,
	Text,
	InlineCode,
	Code,
	Blockquote,
	Emphasis,
	Link,
	LinkReference,
	ListItem,
	Paragraph,
	Root,
	Strong,
	Image,
} from 'mdast'
import { MathComponent } from 'mathjax-react'
import {
	Annotations,
	combineAnnotationsIntoDisjointSections,
	getInlineCodeInformation,
} from './helpers'
import {
	getHighlightAndUnderlineStyle,
	getStrikethroughStyle,
} from './annotationStyling'
import {
	CLOSE_ANNOTATION_HELPERS,
	RangeSelectionData,
} from '@mission.io/question-toolkit'
import {
	findAnnotationRootElement,
	getLocationOfMarkdownCharacter,
	getSelectableCharacterClosestToPoint,
} from './domHelpers'
import { EventTrackerRef } from '../Markdown/hooks'
import classNames from 'classnames'
import {
	AttributeSanitizerMap,
	DEFAULT_SANITIZER,
	HTMLAttributes,
	sanitizeHTMLAttributes,
	SANITIZERS,
} from './htmlAttributeSanitizers'
import { MarkdownTypes } from '@mission.io/question-views'

type MarkdownContext = MarkdownTypes.MarkdownContext

export const MOUSE_SELECTION_LENIENCY = 10 // px - the amount of pixels the mouse can be away from the closest character while still selecting the character
export const TOUCH_SELECTION_LENIENCY = 32 // px - the amount of pixels a touch can be away from the closest character while still selecting the character

const errorMessage = <div>An error occurred displaying this text.</div>

const DEFAULT_ALLOWED_HTML_ELEMENTS = [
	'a',
	'b',
	'blockquote',
	'br',
	'code',
	'del',
	'div',
	'em',
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
	'hr',
	'i',
	'img',
	'li',
	'mark',
	'ol',
	'p',
	'pre',
	'q',
	's',
	'span',
	'strong',
	'sub',
	'sup',
	'u',
	'ul',
	'wbr',
]

type Point = { x: number; y: number }

type PaddlePosition = {
	left: number
	top: number // px - number from the top of the closest relative positioned element
	bottom: number // px - number from the bottom of the closest relative positioned element
	width: number
	height: number
}

type ManualSelection = {
	start: Point // the location where the student first clicked/touched
	end: Point // the location where the student released the mouse/touch
	leniency: number // the number of pixels around the click positions which to check for closest character
}

const LONG_PRESS_DURATION = 400 // ms - how many milliseconds a user must press and hold before considering the action a selection action instead of a tap or scroll
const TOUCH_SCROLL_DISPLACEMENT = 20 // px - how many pixels the user's touch movement must exceed before the `LONG_PRESS_DURATION` elapses to consider the action a scroll instead of a click or select

const NO_ANNOTATIONS: Annotations = []

export type ReadOnlyProps = {
	markdown: string
	annotations?: Annotations
	className?: string
	disabledHtmlTags?: Array<string>
	customJSXElements?: MarkdownTypes.CustomJSXElementLookup
	selectableTextClassName?: string
	rootClassName?: string
}

export type AnnotationUpdateProps = {
	onRangeSelection: (rangeSelection: RangeSelectionData) => unknown
	onRangeSelectionPreview: (rangeSelection: RangeSelectionData) => Annotations
	onAnnotationRangeUpdatePreview: (
		annotationId: string,
		rangeSelection: RangeSelectionData
	) => Annotations
	onAnnotationRangeUpdate: (
		annotationId: string,
		rangeSelection: RangeSelectionData
	) => unknown
	selectedAnnotationId: string | null
	eventTrackerRef: EventTrackerRef
}

const DEFAULT_SELECTABLE_TEXT_CLASS_NAME = 'selectable-text'

/**
 * AnnotatedMarkdown - a react component that allows rendering markdown with annotations
 *
 * @param {Object} props - the react props
 *
 * GENERAL PROPS
 * @param {string} props.markdown - the markdown to render the annotations on
 * @param {Annotations} props.annotations - the annotations to render on the markdown
 * @param {string} props.className? - the className to apply to the markdown
 * @param {Array<string>} props.disabledHtmlTags? - html tags which should not be rendered to the screen (Note: only tags in `DEFAULT_ALLOWED_HTML_ELEMENTS` can ever be rendered to the screen)  (Note: `span` and `div`s are always allowed)
 * @param {CustomJSXElementLookup} props.customJSXElements? - a mapping of a tag to the function component used to render the tag, will take priority over default html tag renderers
 *
 * READ ONLY PROPS
 * @param {true} props.readOnly - if true, then this component will not allow editing of annotations [THIS IS PURELY FOR TYPESCRIPT]
 *
 * WRITE PROPS (only necessary if `readOnly` is false or undefined)
 * @param {false} props.readOnly - if false, then this component will allow creating and editing annotations [THIS IS PURELY FOR TYPESCRIPT]
 * @param {(rangeSelection: RangeSelectionData) => unknown} props.onRangeSelect - a function called when a user selects a range in the markdown. The range prop contains the range of the markdown selected. If not provided, then this will be considered a "read-only" display and annotating will be disabled.
 * @param {(rangeSelection: RangeSelectionData) => Annotations} props.onRangeSelectionPreview - a function called when a user is selecting a range. Annotations returned from this function will appear to the user as they are selecting a range. Will only be called when the selecting range changes. Will never be called if `onRangeSelect` is not given.
 * @param {(annotationId: string, rangeSelection: RangeSelectionData) => Annotations} props.onAnnotationRangeUpdatePreview - a callback used to get temporary annotations to show when the range of a annotation is being updated
 * @param {(annotationId: string, rangeSelection: RangeSelectionData) => unknown} props.onAnnotationRangeUpdate - a callback that is called once the user has finished modifying the range of an annotation
 * @param {string | null} props.selectedAnnotationId - the id of the currently selected annotation (ie. the annotation to show range paddles for)
 * @param {EventTrackerRef} props.eventTrackerRef - an object which keeps track of the events handled in the current evaluation loop (this is necessary due to "click" emitting multiple other events which can cause problems, especially on touch screens)
 * @param {string} props.selectableTextClassName? - a class name to add to any text which is electable
 *
 * @return {JSX.Element}
 */
export function AnnotatedMarkdown(
	props: ReadOnlyProps &
		({ readOnly: true } | (AnnotationUpdateProps & { readOnly: false }))
): JSX.Element {
	const {
		markdown,
		annotations = NO_ANNOTATIONS,
		onRangeSelection,
		onRangeSelectionPreview,
		onAnnotationRangeUpdatePreview,
		onAnnotationRangeUpdate,
		className,
		selectedAnnotationId,
		eventTrackerRef,
		disabledHtmlTags,
		customJSXElements,
		rootClassName,
		selectableTextClassName = DEFAULT_SELECTABLE_TEXT_CLASS_NAME,
	} = props as ReadOnlyProps & Partial<AnnotationUpdateProps>

	const [temporaryAnnotations, setTemporaryAnnotations] = useState<{
		annotations: Annotations
		previousSelectedRange: RangeSelectionData
	} | null>(null)
	const relativeRoot = useRef<HTMLDivElement>(null)
	const markdownContainer = useRef<HTMLDivElement>(null)

	const {
		ast,
		rangeTransformer,
		getClosestSelectableCharacter,
	} = useMemo(() => {
		try {
			const ast = CLOSE_ANNOTATION_HELPERS.parseMarkdownToAST(markdown)

			const selectableElements = CLOSE_ANNOTATION_HELPERS.selectableRangesGenerators.getVisibleWordsFromAst(
				ast
			)

			return {
				ast,
				// select by word
				rangeTransformer: CLOSE_ANNOTATION_HELPERS.rangeTransformers.selectWholeElementRangeTransformer(
					selectableElements
				),
				getClosestSelectableCharacter: (
					point: { x: number; y: number },
					container: Element,
					leniency: number
				) =>
					getSelectableCharacterClosestToPoint(
						point,
						container,
						selectableElements,
						leniency
					),
			}
		} catch (err) {
			console.error(err)
		}
		return {
			ast: null,
			rangeTransformer: () => null,
			getClosestSelectableCharacter: () => null,
		}
	}, [markdown])

	const allowedHtmlElements = useMemo(() => {
		const allowedHtmlElements = new Set(DEFAULT_ALLOWED_HTML_ELEMENTS)
		disabledHtmlTags?.forEach(tag => allowedHtmlElements.delete(tag))

		return allowedHtmlElements
	}, [disabledHtmlTags])

	const jsx = useMemo(() => {
		try {
			if (ast == null) {
				return errorMessage
			}
			return visitNode(
				ast,
				{
					allowedHtmlElements,
					customJSXElements: customJSXElements ?? {},
					annotations: combineAnnotationsIntoDisjointSections(annotations),
					selectableTextClassName,
					rootClassName,
				},
				'root'
			)
		} catch (err) {
			return errorMessage
		}
	}, [
		ast,
		annotations,
		allowedHtmlElements,
		customJSXElements,
		selectableTextClassName,
		rootClassName,
	])

	const annotationPreview = useMemo(() => {
		try {
			if (ast == null || temporaryAnnotations == null) {
				return null
			}
			return visitNode(
				ast,
				{
					allowedHtmlElements,
					customJSXElements: customJSXElements ?? {},
					annotations: combineAnnotationsIntoDisjointSections(
						temporaryAnnotations.annotations
					),
					selectableTextClassName,
					rootClassName,
				},
				'root'
			)
		} catch (err) {
			return null
		}
	}, [
		ast,
		temporaryAnnotations,
		allowedHtmlElements,
		customJSXElements,
		selectableTextClassName,
		rootClassName,
	])

	/**
	 * getSelectionRange - get the selected range in the markdown text (returns ranges within the markdown string). null if no part of the selectable markdown has been selected
	 *
	 * @param {Object} manualSelection - the start and end coordinates of the users selection, if not given, will fallback to window.getSelection ranges
	 *
	 * @return {RangeSelectionData | null}
	 */
	function getSelectionRange(
		manualSelection?: ManualSelection
	): RangeSelectionData | null {
		if (!onRangeSelection || !manualSelection) {
			return null
		}

		return getSelectionRangeFromManualSelection(manualSelection)
	}

	/**
	 * getSelectionRangeFromManualSelection - get a selected markdown range between two document x,y selection points
	 *
	 * @param {ManualSelection} manualSelection - the start and end coordinates of the users selection, if not given.
	 *
	 * @return {RangeSelectionData | null} null if no selection happened
	 */
	function getSelectionRangeFromManualSelection(
		manualSelection: ManualSelection
	): RangeSelectionData | null {
		const markdownRoot = markdownContainer.current
		if (!markdownRoot) {
			return null
		}

		const closestToStartPoint = getClosestSelectableCharacter(
			manualSelection.start,
			markdownRoot,
			manualSelection.leniency
		)
		const closestToEndPoint =
			manualSelection.start.x === manualSelection.end.x &&
			manualSelection.start.y === manualSelection.end.y
				? closestToStartPoint
				: getClosestSelectableCharacter(
						manualSelection.end,
						markdownRoot,
						manualSelection.leniency
				  )

		if (!closestToStartPoint || !closestToEndPoint) {
			setTemporaryAnnotations(null)
			return null
		}

		return rangeTransformer({
			start: Math.min(
				closestToStartPoint.markdownIndex,
				closestToEndPoint.markdownIndex
			),
			end:
				Math.max(
					closestToStartPoint.markdownIndex,
					closestToEndPoint.markdownIndex
				) + 1, // + 1 as end is exclusive
		})
	}

	/**
	 * submitRangeSelection - determine the selected markdown index range and notify the caller
	 *
	 * @param {ManualSelection} manualSelection - the start and end coordinates of the users selection, if not given, will fallback to window.getSelection ranges
	 */
	function submitRangeSelection(manualSelection: ManualSelection) {
		const rangeData = getSelectionRange(manualSelection)
		window.getSelection()?.removeAllRanges()
		setTemporaryAnnotations(null)
		if (rangeData == null || !onRangeSelection) {
			return null
		}
		onRangeSelection(rangeData)
	}

	/**
	 * previewRangeSelection - update the annotation preview to see what the markdown would look like if the currently selected range is submitted
	 *
	 *  @param {ManualSelection} manualSelection - the start and end coordinates of the users selection, if not given, will fallback to window.getSelection ranges
	 */
	function previewRangeSelection(manualSelection: ManualSelection) {
		const rangeData = getSelectionRange(manualSelection)
		if (
			rangeData == null ||
			!onRangeSelection ||
			!onRangeSelectionPreview ||
			(rangeData.range.start ===
				temporaryAnnotations?.previousSelectedRange.range.start &&
				rangeData.range.end ===
					temporaryAnnotations?.previousSelectedRange.range.end)
		) {
			return null
		}

		setTemporaryAnnotations({
			annotations: onRangeSelectionPreview(rangeData),
			previousSelectedRange: rangeData,
		})
	}

	const originalSelectedAnnotation = annotations.find(
		({ id }) => id === selectedAnnotationId
	)
	const selectedAnnotation =
		temporaryAnnotations?.annotations.find(
			({ id }) => id === selectedAnnotationId
		) || originalSelectedAnnotation
	const paddleLocations: {
		start: PaddlePosition
		end: PaddlePosition
	} | null = useMemo(() => {
		if (!selectedAnnotation) {
			return null
		}

		const container = markdownContainer.current
		const relativeRootElement = relativeRoot.current
		if (!container || !relativeRootElement) {
			return null
		}

		const relativeRootBox = relativeRootElement.getBoundingClientRect()

		const startLocation = getLocationOfMarkdownCharacter(
			container,
			selectedAnnotation.range.start
		)

		const endLocation = getLocationOfMarkdownCharacter(
			container,
			selectedAnnotation.range.end - 1
		)

		if (!startLocation || !endLocation) {
			return null
		}

		return {
			start: {
				left: startLocation.left - relativeRootBox.left,
				top: startLocation.top - relativeRootBox.top,
				bottom: relativeRootBox.bottom - startLocation.bottom,
				width: startLocation.width,
				height: startLocation.height,
			},
			end: {
				left: endLocation.left - relativeRootBox.left,
				top: endLocation.top - relativeRootBox.top,
				bottom: relativeRootBox.bottom - endLocation.bottom,
				width: endLocation.width,
				height: endLocation.height,
			},
		}
	}, [selectedAnnotation])

	/**
	 * onPaddleMove - handle updating the annotations when a paddle is moved
	 *
	 * @param {'start' | 'end'} paddleBeingMoved - 'start' if the paddle being moved represents the start of the annotation, 'end' if the paddle represents the end of the annotation
	 * @param {Point} mouseLocation - the current location of the mouse/touch on the page (clientX, and ClientY)
	 * @param {'mouse' | 'touch'} by - what is changing the mouseLocation (ie. by mouse or touch)
	 */
	function onPaddleMove(
		paddleBeingMoved: 'start' | 'end',
		mouseLocation: Point,
		by: 'mouse' | 'touch'
	): {
		rangeSelection: RangeSelectionData
		annotationId: string
	} | null {
		if (
			!originalSelectedAnnotation ||
			!onRangeSelectionPreview ||
			!selectedAnnotationId
		) {
			return null
		}

		const element = document.elementFromPoint(mouseLocation.x, mouseLocation.y)
		if (!element) {
			return null
		}

		const annotationRoot = findAnnotationRootElement(element)
		if (!annotationRoot) {
			return null
		}

		const closestPoint = getClosestSelectableCharacter(
			mouseLocation,
			annotationRoot,
			by === 'mouse' ? MOUSE_SELECTION_LENIENCY : TOUCH_SELECTION_LENIENCY
		)

		if (!closestPoint) {
			return null
		}

		const closestMarkdownIndex = closestPoint.markdownIndex

		let startIndex, endIndex
		if (paddleBeingMoved === 'start') {
			if (closestMarkdownIndex >= originalSelectedAnnotation.range.end) {
				startIndex = originalSelectedAnnotation.range.end
				endIndex = closestMarkdownIndex
			} else {
				startIndex = closestMarkdownIndex
				endIndex = originalSelectedAnnotation.range.end
			}
		} else {
			if (closestMarkdownIndex < originalSelectedAnnotation.range.start) {
				startIndex = closestMarkdownIndex
				endIndex = originalSelectedAnnotation.range.start
			} else {
				startIndex = originalSelectedAnnotation.range.start
				endIndex = closestMarkdownIndex
			}
		}

		if (startIndex === endIndex) {
			return null
		}

		const baseRange = {
			start: startIndex,
			end: endIndex,
		}

		const range = rangeTransformer(baseRange)

		if (!range) {
			return null
		}

		return {
			rangeSelection: range,
			annotationId: selectedAnnotationId,
		}
	}

	const unmountAbortController = useMemo(() => new AbortController(), [])

	return (
		<MarkdownErrorBoundary markdown={markdown}>
			<div
				className={className}
				onMouseDown={event => {
					if (
						!eventTrackerRef?.current ||
						eventTrackerRef.current
							.firstEventTypeHandledInCurrentEvaluationLoop || // NOTE: due to event ordering, this means that a touch event occurred and this happened after
						selectedAnnotationId
					) {
						// something is already handling this event, this can occur in situations such as `onClick` which will always run `touchStart`, `touchEnd`, `mouseDown`, `mouseUp` in that order even if prevent default is called on the event
						return
					}

					eventTrackerRef.current.noteEventType(event.type)

					const abortController = new AbortController()
					const signal = abortSignalAnyPolyfill([
						abortController.signal,
						unmountAbortController.signal,
					])

					window.addEventListener(
						'mousemove',
						event => {
							eventTrackerRef.current?.noteEventType(event.type)
							previewRangeSelection({
								start: startPoint,
								end: { x: event.clientX, y: event.clientY },
								leniency: MOUSE_SELECTION_LENIENCY,
							})
						},
						{
							signal,
						}
					)

					const startPoint = {
						x: event.clientX,
						y: event.clientY,
					}

					window.addEventListener(
						'mouseup',
						event => {
							eventTrackerRef.current?.noteEventType(event.type)
							abortController.abort()
							submitRangeSelection({
								start: startPoint,
								end: { x: event.clientX, y: event.clientY },
								leniency: MOUSE_SELECTION_LENIENCY,
							})
						},
						{
							signal,
						}
					)
				}}
				onTouchStart={event => {
					if (
						!eventTrackerRef?.current ||
						eventTrackerRef.current
							.firstEventTypeHandledInCurrentEvaluationLoop ||
						selectedAnnotationId
					) {
						// something is already handling this event, this can occur in situations such as `onClick` which will always run `touchStart`, `touchEnd`, `mouseDown`, `mouseUp` in that order even if prevent default is called on the event
						return
					}

					eventTrackerRef.current.noteEventType(event.type)
					const lastTouchEvent =
						event.changedTouches[event.changedTouches.length - 1]

					if (!lastTouchEvent) {
						return
					}
					const startPoint = {
						x: lastTouchEvent.clientX,
						y: lastTouchEvent.clientY,
					}

					event.preventDefault()

					const abortController = new AbortController()
					const signal = abortSignalAnyPolyfill([
						abortController.signal,
						unmountAbortController.signal,
					])
					const startTime = Date.now()

					let lastTouchPointer = startPoint
					const timerId = setTimeout(() => {
						if (signal.aborted) {
							return
						}

						previewRangeSelection({
							start: startPoint,
							end: lastTouchPointer,
							leniency: TOUCH_SELECTION_LENIENCY,
						})
					}, LONG_PRESS_DURATION)

					/**
					 * abort - removes the temporary event handlers and timers
					 */
					function abort() {
						abortController.abort()
						clearTimeout(timerId)
					}

					event.preventDefault()
					window.addEventListener(
						'touchmove',
						event => {
							eventTrackerRef.current?.noteEventType(event.type)
							const lastTouchEvent =
								event.changedTouches[event.changedTouches.length - 1]
							if (!lastTouchEvent) {
								return
							}

							lastTouchPointer = {
								x: lastTouchEvent.clientX,
								y: lastTouchEvent.clientY,
							}

							if (Date.now() <= startTime + LONG_PRESS_DURATION) {
								// not currently selecting
								if (
									Math.sqrt(
										(startPoint.x - lastTouchEvent.clientX) ** 2 +
											(startPoint.y - lastTouchEvent.clientY) ** 2
									) >= TOUCH_SCROLL_DISPLACEMENT
								) {
									// user is trying to scroll the window, not select
									setTemporaryAnnotations(null)
									abort()
								}
								return
							}
							event.preventDefault()
							previewRangeSelection({
								start: startPoint,
								end: lastTouchPointer,
								leniency: TOUCH_SELECTION_LENIENCY,
							})
						},
						{ signal, passive: false }
					)

					window.addEventListener(
						'touchend',
						event => {
							eventTrackerRef.current?.noteEventType(event.type)
							event.preventDefault()
							abort()

							const lastTouchEvent =
								event.changedTouches[event.changedTouches.length - 1]

							if (!lastTouchEvent) {
								// this should not be possible
								return
							}

							submitRangeSelection({
								start: startPoint,
								end: {
									x: lastTouchEvent.clientX,
									y: lastTouchEvent.clientY,
								},
								leniency: TOUCH_SELECTION_LENIENCY,
							})
						},
						{ signal, passive: false }
					)
				}}>
				<div
					className={classNames(
						'prose prose-img:max-w-full prose-markdown max-w-none relative p-0 m-0',
						className
					)}
					ref={relativeRoot}>
					{/* Note: the padding and margin are used to stop margin collapsing (https://www.geeksforgeeks.org/how-to-disable-margin-collapsing-in-css/) so that the markdown and the annotation preview align correctly. Also p-0 and m-0 are marked as important in simulation-creator thanks to bootstrap, hence the p-[0px] and m-[0px]. */}
					<div className="pt-[1px] -mt-[1px] p-[0px] m-[0px]">
						<div
							className={classNames(
								'm-0 p-0 w-full',
								props.readOnly ? '' : 'selection:bg-transparent select-none'
							)}
							ref={markdownContainer}>
							{annotationPreview ?? jsx}
						</div>
						{selectedAnnotationId &&
						paddleLocations &&
						eventTrackerRef &&
						onAnnotationRangeUpdatePreview &&
						onAnnotationRangeUpdate
							? (['start', 'end'] as const).map(paddleLocation => (
									<Paddle
										key={paddleLocation}
										clickable={!temporaryAnnotations}
										position={
											paddleLocation === 'start'
												? paddleLocations.start
												: paddleLocations.end
										}
										onPaddleMove={(point, by) => {
											const selectedRangeData = onPaddleMove(
												paddleLocation,
												point,
												by
											)
											const previouslySelectedRange =
												temporaryAnnotations?.previousSelectedRange

											if (
												!selectedRangeData ||
												// no visible change
												(previouslySelectedRange &&
													selectedRangeData.rangeSelection.range.start ===
														previouslySelectedRange.range.start &&
													selectedRangeData.rangeSelection.range.end ===
														previouslySelectedRange.range.end)
											) {
												return
											}

											setTemporaryAnnotations({
												annotations: onAnnotationRangeUpdatePreview(
													selectedAnnotationId,
													selectedRangeData.rangeSelection
												),
												previousSelectedRange: selectedRangeData.rangeSelection,
											})
										}}
										onPaddleRelease={releaseType => {
											const currentPaddleRange =
												temporaryAnnotations?.previousSelectedRange
											setTemporaryAnnotations(null)

											if (currentPaddleRange == null || releaseType == null) {
												return
											}

											onAnnotationRangeUpdate(
												selectedAnnotationId,
												currentPaddleRange
											)
										}}
										location={paddleLocation}
										eventTrackerRef={eventTrackerRef}
									/>
							  ))
							: null}
					</div>
				</div>
			</div>
		</MarkdownErrorBoundary>
	)
}

/**
 * Paddle - a paddle which represents the start or end of an annotation and can be moved to change the range of the annotation
 *
 * @param {Object} props - the react props
 * @param {PaddlePosition} props.position - the location of the character the paddle is positioned at
 * @param {'start' | 'end'} props.location - 'start' if the paddle represents the start of the annotation, 'end' if the paddle is at the end of the annotation
 * @param {boolean} props.clickable - determines if the paddle is currently clickable (ie. able to be selected and moved)
 * @param {(newLocation: Point, by: 'mouse' | 'touch') => unknown} props.onPaddleMove - a callback which is called when the paddle is moved
 * @param {(by: 'mouse' | 'touch' | null) => unknown} props.onPaddleRelease - a callback is called when the paddle is released (called with 'mouse' or 'touch') or is unmounted (called with null).
 * @param {EventTrackerRef} props.eventTrackerRef - an object which keeps track of the events handled within this event cycle
 *
 * @returns {React.JSX.Element}
 */
function Paddle({
	position,
	onPaddleMove,
	onPaddleRelease,
	clickable,
	location,
	eventTrackerRef,
}: {
	position: PaddlePosition
	onPaddleMove: (newLocation: Point, by: 'mouse' | 'touch') => unknown
	onPaddleRelease: (by: 'mouse' | 'touch' | null) => unknown // null means the paddle component was unmounted
	clickable: boolean
	location: 'start' | 'end'
	eventTrackerRef: EventTrackerRef
}) {
	const onPaddleMoveRef = useRef(onPaddleMove)
	onPaddleMoveRef.current = onPaddleMove

	const onPaddleReleaseRef = useRef(onPaddleRelease)
	onPaddleReleaseRef.current = onPaddleRelease

	const umountAbortController = useMemo(() => new AbortController(), [])
	useEffect(() => {
		return () => umountAbortController.abort()
	}, [])

	const LINE_WIDTH = 2
	const LINE_BUFFER = 1

	return (
		<>
			<div
				className="absolute bg-info opacity-50 pointer-events-none"
				style={{
					left:
						position.left +
						(location === 'start' ? 0 : position.width) -
						LINE_WIDTH / 2,
					height: position.height + LINE_BUFFER,
					width: LINE_WIDTH,

					...(location === 'start'
						? { bottom: position.bottom }
						: { top: position.top }),
				}}
			/>
			<div
				className={classNames(
					'absolute w-4 h-4 -translate-x-2 bg-info rounded-full hover:brightness-125 cursor-grab',
					!clickable ? 'pointer-events-none' : ''
				)}
				onTouchStart={event => {
					if (
						eventTrackerRef.current
							?.firstEventTypeHandledInCurrentEvaluationLoop != null
					) {
						return
					}
					eventTrackerRef.current?.noteEventType(event.type)

					const controller = new AbortController()
					const signal = abortSignalAnyPolyfill([
						controller.signal,
						umountAbortController.signal,
					])

					window.addEventListener(
						'touchmove',
						event => {
							eventTrackerRef.current?.noteEventType(event.type)
							event.preventDefault()

							const touchChange = event.changedTouches[0]
							if (!touchChange) {
								return
							}

							onPaddleMoveRef.current(
								{
									x: touchChange.clientX,
									y: touchChange.clientY,
								},
								'touch'
							)
						},
						{ signal, passive: false }
					)

					const handleAbort = () => {
						onPaddleReleaseRef.current(null)
					}
					signal.addEventListener('abort', handleAbort) // handle abort due to umount

					window.addEventListener(
						'touchend',
						event => {
							eventTrackerRef.current?.noteEventType(event.type)
							signal.removeEventListener('abort', handleAbort)

							controller.abort()

							onPaddleReleaseRef.current('touch')
							window.getSelection()?.removeAllRanges()
						},
						{ signal }
					)
				}}
				onMouseDown={event => {
					if (
						// this is to avoid some problems with touch screens. When a user taps quickly it fires the following series of events `touchUp`, `mouseDown`, `mouseUp`, `click` which as `mouseDown` is on that list, will cause this function to be called. (NOTE: it is not possible to avoid this even by using `preventDefault` and `stopPropagation`)
						eventTrackerRef.current
							?.firstEventTypeHandledInCurrentEvaluationLoop != null
					) {
						return
					}
					eventTrackerRef.current?.noteEventType(event.type)

					const controller = new AbortController()
					const signal = abortSignalAnyPolyfill([
						controller.signal,
						umountAbortController.signal,
					])

					window.addEventListener(
						'mousemove',
						event => {
							eventTrackerRef.current?.noteEventType(event.type)
							onPaddleMoveRef.current(
								{ x: event.clientX, y: event.clientY },
								'mouse'
							)
						},
						{ signal }
					)

					const handleAbort = () => {
						onPaddleReleaseRef.current(null)
					}
					signal.addEventListener('abort', handleAbort) // handle abort due to umount

					window.addEventListener(
						'mouseup',
						event => {
							eventTrackerRef.current?.noteEventType(event.type)

							signal.removeEventListener('abort', handleAbort)
							controller.abort()

							onPaddleReleaseRef.current('mouse')
							window.getSelection()?.removeAllRanges()
						},
						{ signal }
					)
				}}
				onClick={event => {
					eventTrackerRef.current?.noteEventType(event.type)
				}}
				style={
					location === 'start'
						? {
								left: position.left,
								bottom: position.bottom + position.height + LINE_BUFFER,
						  }
						: {
								left: position.left + position.width,
								top: position.top + position.height + LINE_BUFFER,
						  }
				}
			/>
		</>
	)
}

/**
 * MathRenderer - a react component used to render math/latex syntax
 *
 * @param {Object} props - the react props
 * @param {InlineCode | Code} props.node - the mdast code node to render
 * @param {MarkdownContext} props.context - the context that this markdown is rendering in
 *
 * @return {JSX.Element}
 */
function MathRenderer({
	node,
}: {
	node: InlineCode | Code
	context: MarkdownContext
}): JSX.Element {
	const isBlock = node.type === 'code'
	const component = <MathComponent tex={node.value} display={isBlock} />
	if (isBlock) {
		return component
	}
	return <span className="*:inline-block *:align-text-top">{component}</span>
}

/**
 * A mapping of the supported code languages to components which can render the language
 */
const LanguageRenderers = {
	math: MathRenderer,
	katex: MathRenderer,
	latex: MathRenderer,
	code: ({
		node,
		context,
	}: {
		node: InlineCode | Code
		context: MarkdownContext
	}): JSX.Element => {
		return (
			<code
				x-mission-io-annotation-start-index={node.position?.start.offset}
				x-mission-io-annotation-end-index={node.position?.end.offset}>
				{componentsLookup.text?.({
					node: { type: 'text', value: node.value, position: node.position },
					context,
				}) ?? null}
			</code>
		)
	},
}

type Component<T extends Node> = (props: {
	node: T
	context: MarkdownContext
}) => JSX.Element | null

/**
 * BaseHtmlComponent - a function which returns a component used to render a mdast node inside of a basic html component.
 *
 * @param {string} ComponentName - the name of the base html component to render the node in
 *
 * @return {Component}
 */
function BaseHtmlComponent<
	T extends
		| Blockquote
		| Emphasis
		| Heading
		| Link
		| LinkReference
		| List
		| ListItem
		| Paragraph
		| Strong
		| MdxJsxFlowElement
>(
	componentName:
		| 'div'
		| 'u'
		| 'b'
		| 'i'
		| 'p'
		| 'li'
		| 'blockquote'
		| 'strong'
		| 'code'
		| 'i'
		| 'del'
): Component<T> {
	return ({
		node,
		context,
	}: {
		node: T
		context: MarkdownContext
	}): JSX.Element => {
		const Component = getHTMLComponentToUse(componentName, context)
		return (
			<Component
				x-mission-io-annotation-start-index={node.position?.start.offset}
				x-mission-io-annotation-end-index={node.position?.end.offset}>
				{renderChildren(node.children, context)}
			</Component>
		)
	}
}

/**
 * BaseSelfClosingHtmlComponent - a function which returns a component used to render a mdast node which translates to a self closing html tag.
 *
 * @param {string} ComponentName - the name of the base html tag to render
 *
 * @return {Component}
 */
function BaseSelfClosingHtmlComponent<T extends MdxJsxFlowElement>(
	ComponentName: 'br'
): Component<T> {
	return ({
		node,
		context,
	}: {
		node: T
		context: MarkdownContext
	}): JSX.Element | null => {
		if (!context.allowedHtmlElements.has(ComponentName)) {
			return null
		}
		return (
			<ComponentName
				x-mission-io-annotation-start-index={node.position?.start.offset}
				x-mission-io-annotation-end-index={node.position?.end.offset}
			/>
		)
	}
}

const NotAllowed = BaseHtmlComponent('div')

/**
 * BaseHtmlComponentFromJSX - generates a react component which can render the given html tag based off the `MdxJsxFlowElement` node
 *
 * @param {string} tag - the html tag to render
 * @param {Object} options? - other options for the component
 * @param {AttributeSanitizerMap} options.attributeSanitizers? - a mapping of allowed attributes to the sanitizers used to sanitize the attributes value
 * @param {HTMLAttributes} options.defaultHtmlAttributes? - any html attributes which should appear on the tag by default. If a sanitizer for the attribute exists in `attributeSanitizers` and a value is passed in via the markdown, that sanitized value will take precedence.
 * @param {boolean} options.isSelfClosing? - true if the tag is self closing and should have no decedents (false by default)
 *
 * @returns {Component<MdxJsxFlowElement>}
 */
function BaseHtmlComponentFromJSX(
	tag:
		| 'a'
		| 'b'
		| 'blockquote'
		| 'br'
		| 'code'
		| 'del'
		| 'div'
		| 'em'
		| 'h1'
		| 'h2'
		| 'h3'
		| 'h4'
		| 'h5'
		| 'h6'
		| 'hr'
		| 'i'
		| 'img'
		| 'li'
		| 'mark'
		| 'ol'
		| 'p'
		| 'pre'
		| 'q'
		| 's'
		| 'span'
		| 'strong'
		| 'sub'
		| 'sup'
		| 'u'
		| 'ul'
		| 'wbr',
	{
		attributeSanitizers = DEFAULT_SANITIZER,
		isSelfClosing = false,
		defaultHtmlAttributes,
	}: {
		attributeSanitizers?: AttributeSanitizerMap
		isSelfClosing?: boolean
		defaultHtmlAttributes?: HTMLAttributes
	} = {}
): Component<MdxJsxFlowElement> {
	return ({
		node,
		context,
	}: {
		node: MdxJsxFlowElement
		context: MarkdownContext
	}) => {
		const children = !isSelfClosing
			? renderChildren(node.children, context)
			: null
		if (!context.allowedHtmlElements.has(tag)) {
			return <>{children}</>
		}

		const Component = tag
		const attributes =
			(attributeSanitizers &&
				sanitizeHTMLAttributes(node, attributeSanitizers)) ??
			{}

		return (
			<Component
				{...defaultHtmlAttributes}
				{...attributes}
				x-mission-io-annotation-start-index={node.position?.start.offset}
				x-mission-io-annotation-end-index={node.position?.end.offset}>
				{children}
			</Component>
		)
	}
}

/**
 * a mapping of html tag names to components which can render the html component
 */
const htmlTagsLookup: Record<
	string, // html tag name
	Component<MdxJsxFlowElement>
> = {
	a: BaseHtmlComponentFromJSX('a', {
		attributeSanitizers: new Map([
			...DEFAULT_SANITIZER.entries(),
			['href', SANITIZERS.href],
		]),
		defaultHtmlAttributes: { target: '_blank' },
	}),
	b: BaseHtmlComponentFromJSX('b'),
	blockquote: BaseHtmlComponentFromJSX('blockquote', {
		attributeSanitizers: new Map([
			...DEFAULT_SANITIZER.entries(),
			['cite', SANITIZERS.cite],
		]),
	}),
	br: BaseHtmlComponentFromJSX('br', { isSelfClosing: true }),
	code: BaseHtmlComponentFromJSX('code'),
	del: BaseHtmlComponentFromJSX('del'),
	div: BaseHtmlComponentFromJSX('div'),
	em: BaseHtmlComponentFromJSX('em'),
	h1: BaseHtmlComponentFromJSX('h1'),
	h2: BaseHtmlComponentFromJSX('h2'),
	h3: BaseHtmlComponentFromJSX('h3'),
	h4: BaseHtmlComponentFromJSX('h4'),
	h5: BaseHtmlComponentFromJSX('h5'),
	h6: BaseHtmlComponentFromJSX('h6'),
	hr: BaseHtmlComponentFromJSX('hr', { isSelfClosing: true }),
	i: BaseHtmlComponentFromJSX('i'),
	img: BaseHtmlComponentFromJSX('img', {
		attributeSanitizers: new Map([
			...DEFAULT_SANITIZER.entries(),
			['src', SANITIZERS.imageSrc],
		]),
		isSelfClosing: true,
	}),
	li: BaseHtmlComponentFromJSX('li'),
	mark: BaseHtmlComponentFromJSX('mark'),
	ol: BaseHtmlComponentFromJSX('ol'),
	p: BaseHtmlComponentFromJSX('p'),
	pre: BaseHtmlComponentFromJSX('pre'),
	q: BaseHtmlComponentFromJSX('q'),
	s: BaseHtmlComponentFromJSX('s'),
	span: BaseHtmlComponentFromJSX('span'),
	strong: BaseHtmlComponentFromJSX('strong'),
	sub: BaseHtmlComponentFromJSX('sub'),
	sup: BaseHtmlComponentFromJSX('sup'),
	u: BaseHtmlComponentFromJSX('u'),
	ul: BaseHtmlComponentFromJSX('ul'),
	wbr: BaseHtmlComponentFromJSX('wbr'),
}

/**
 * a mapping of mdast node types to components which can render that type of mdast node
 */
const componentsLookup: Record<
	string, // mdast node name
	React.FunctionComponent<{
		node: any
		context: MarkdownContext
	}>
> = {
	paragraph: BaseHtmlComponent('p'),
	root: function Root({
		node,
		context,
	}: {
		node: Root
		context: MarkdownContext
	}): JSX.Element {
		return (
			<div
				x-mission-io-annotation-start-index={node.position?.start.offset}
				x-mission-io-annotation-end-index={node.position?.end.offset}
				x-mission-io-annotations-root="true"
				className={context.rootClassName ?? ''}>
				{renderChildren(node.children, context)}
			</div>
		)
	},
	list: ({ node, context }: { node: List; context: MarkdownContext }) => {
		const Component = getHTMLComponentToUse(node.ordered ? 'ol' : 'ul', context)
		return (
			<Component
				start={node.start ?? undefined}
				className="pl-8"
				x-mission-io-annotation-start-index={node.position?.start.offset}
				x-mission-io-annotation-end-index={node.position?.end.offset}>
				{renderChildren(node.children, context)}
			</Component>
		)
	},
	listItem: BaseHtmlComponent('li'),
	blockquote: BaseHtmlComponent('blockquote'),
	break: BaseSelfClosingHtmlComponent('br'),
	heading: ({ node, context }: { node: Heading; context: MarkdownContext }) => {
		const Component = getHTMLComponentToUse(`h${node.depth}` as const, context)
		return (
			<Component
				x-mission-io-annotation-start-index={node.position?.start.offset}
				x-mission-io-annotation-end-index={node.position?.end.offset}>
				{renderChildren(node.children, context)}
			</Component>
		)
	},
	strong: BaseHtmlComponent('strong'),
	inlineCode: ({
		node,
		context,
	}: {
		node: InlineCode
		context: MarkdownContext
	}) => {
		const {
			codeSnippet,
			language,
			codeSnippetRange,
		} = getInlineCodeInformation(node.value, node.position ?? null)
		const Component =
			language && language in LanguageRenderers
				? LanguageRenderers[language as keyof typeof LanguageRenderers]
				: LanguageRenderers.code

		return (
			<Component
				context={context}
				node={
					{
						type: 'inlineCode',
						value: codeSnippet,
						position: {
							start: { offset: codeSnippetRange?.start },
							end: { offset: codeSnippetRange?.end },
							indent: node.position?.indent,
						},
					} as InlineCode
				}
			/>
		)
	},
	code: ({ node, context }: { node: Code; context: MarkdownContext }) => {
		const language = node.lang
		const Component =
			language && language in LanguageRenderers
				? LanguageRenderers[language as keyof typeof LanguageRenderers]
				: LanguageRenderers.code

		const startPositionOffset =
			node.position?.start.offset != null
				? node.position.start.offset + 3 + (language?.length ?? 0) + 1 // +3 to ignore the triple ticks ```, +1 to ignore the new line after the language
				: undefined

		return (
			<pre>
				<Component
					context={context}
					node={{
						type: 'code',
						value: node.value,
						position:
							node.position != null
								? {
										...node.position,
										start: {
											...node.position.start,
											offset: startPositionOffset,
										},
										end: {
											...node.position.end,
											offset: startPositionOffset
												? startPositionOffset + node.value.length // does not base off of node.position.end because trailing whitespace in a codeblock can be removed by the parser
												: undefined,
										},
								  }
								: undefined,
					}}
				/>
			</pre>
		)
	},
	definition: NotAllowed,
	emphasis: BaseHtmlComponent('i'),
	html: NotAllowed,
	image: ({ node, context }: { node: Image; context: MarkdownContext }) => {
		if (!context.allowedHtmlElements.has('img')) {
			if (node.title) {
				return <span>{node.title}</span>
			}
			return null
		}
		return <img src={node.url} title={node.title ?? undefined} />
	},
	link: ({ node, context }: { node: Link; context: MarkdownContext }) => {
		if (!context.allowedHtmlElements.has('a')) {
			return <>{renderChildren(node.children, context)}</>
		}
		return (
			<a href={node.url} title={node.title ?? undefined}>
				{renderChildren(node.children, context)}
			</a>
		)
	},
	linkReference: NotAllowed,
	thematicBreak: NotAllowed,
	delete: BaseHtmlComponent('del'),
	mdxJsxFlowElement: visitHtmlTagNode,
	mdxJsxTextElement: visitHtmlTagNode,
	text: ({ node, context }: { node: Text; context: MarkdownContext }) => {
		const nodeStartOffset = node.position?.start.offset
		const nodeEndOffset = node.position?.end.offset
		if (nodeStartOffset == null || nodeEndOffset == null) {
			return <span>{node.type}</span>
		}

		const annotations = context.annotations

		const textSlices = []

		// TODO: speed this up with binary search
		let startingAnnotationIndex = 0
		for (
			;
			startingAnnotationIndex < annotations.length;
			startingAnnotationIndex += 1
		) {
			const currentAnnotation = annotations[startingAnnotationIndex]
			// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
			if (currentAnnotation.range.end < nodeStartOffset) {
				continue
			}
			break
		}

		// TODO: speed this up with binary search
		let endingAnnotationIndex = annotations.length - 1
		for (
			endingAnnotationIndex;
			endingAnnotationIndex >= 0;
			endingAnnotationIndex -= 1
		) {
			const currentAnnotation = annotations[endingAnnotationIndex]
			// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
			if (currentAnnotation.range.start > nodeEndOffset) {
				continue
			}
			break
		}

		if (endingAnnotationIndex < startingAnnotationIndex) {
			// no annotations over this text element
			return (
				<span
					x-mission-io-annotation-start-index={nodeStartOffset}
					x-mission-io-annotation-end-index={nodeEndOffset}
					className={context.selectableTextClassName}>
					{node.value}
				</span>
			)
		}

		// apply styles to each annotation seperately
		let lastSliceEndTextIndex = 0
		for (
			let annotationIndex = startingAnnotationIndex;
			annotationIndex <= endingAnnotationIndex;
			annotationIndex += 1
		) {
			const currentAnnotation = annotations[annotationIndex]

			const sliceStartPosition = Math.max(
				// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				currentAnnotation.range.start - nodeStartOffset,
				0
			)

			const sliceEndPosition = Math.min(
				// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				currentAnnotation.range.end - nodeStartOffset,
				node.value.length
			)

			if (lastSliceEndTextIndex < sliceStartPosition) {
				// there is unstyled text between the last annotation and the next annotation, add the text here
				textSlices.push(
					<span
						x-mission-io-annotation-start-index={
							nodeStartOffset + lastSliceEndTextIndex
						}
						x-mission-io-annotation-end-index={
							nodeStartOffset + sliceStartPosition
						}
						className={context.selectableTextClassName}
						key={String(textSlices.length)}>
						{node.value.slice(lastSliceEndTextIndex, sliceStartPosition)}
					</span>
				)
			}

			lastSliceEndTextIndex = sliceEndPosition

			// only apply the necessary styles
			const styles: Array<CSSProperties> = []
			// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
			const strikeThroughStyle = getStrikethroughStyle(currentAnnotation)
			if (strikeThroughStyle) {
				styles.push(strikeThroughStyle)
			}

			const highlightAndUnderlineStyle = getHighlightAndUnderlineStyle(
				// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				currentAnnotation
			)
			if (highlightAndUnderlineStyle) {
				styles.push(highlightAndUnderlineStyle)
			}

			textSlices.push(
				styles.reduce(
					(jsx: JSX.Element, style): JSX.Element => (
						// needed as strike-through and underline both use the css `text-decoration` property which can not have two different text decorations of different colors
						<span
							style={style}
							x-mission-io-annotation-start-index={Math.max(
								// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
								currentAnnotation.range.start,
								nodeStartOffset
							)}
							x-mission-io-annotation-end-index={Math.min(
								// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
								currentAnnotation.range.end,
								nodeEndOffset
							)}
							className={context.selectableTextClassName}
							key={String(textSlices.length)}>
							{jsx}
						</span>
					),
					(
						<>{node.value.slice(sliceStartPosition, sliceEndPosition)}</>
					) as JSX.Element
				)
			)
		}

		if (lastSliceEndTextIndex < node.value.length) {
			// an annotation did not include the end of the text in this span
			textSlices.push(
				<span
					x-mission-io-annotation-start-index={
						nodeStartOffset + lastSliceEndTextIndex
					}
					x-mission-io-annotation-end-index={nodeEndOffset}
					className={context.selectableTextClassName}
					key={String(textSlices.length)}>
					{node.value.slice(lastSliceEndTextIndex)}
				</span>
			)
		}

		return (
			<span
				x-mission-io-annotation-start-index={nodeStartOffset}
				x-mission-io-annotation-end-index={nodeEndOffset}
				className={context.selectableTextClassName}>
				{textSlices}
			</span>
		)
	},
}

/**
 * visitHtmlTagNode - a function used to visit and render an mdast html node (MdxJsxFlowElement)
 *
 * @param {Object} props - the react props
 * @param {MdxJsxFlowElement} props.node - the mdast html node (MdxJsxFlowElement) to render
 * @param {MarkdownContext} props.context - the context that this markdown is rendering in
 *
 * @return {JSX.Element}
 */
function visitHtmlTagNode({
	node,
	context,
}: {
	node: MdxJsxFlowElement
	context: MarkdownContext
}): JSX.Element {
	const Component =
		(node.name
			? context.customJSXElements[node.name] ??
			  (node.name in htmlTagsLookup ? htmlTagsLookup[node.name] : null)
			: null) ?? NotAllowed
	return <Component node={node} context={context} />
}

/**
 * visitNode - a function used to visit and render an mdast node
 *
 * @param {Node} node - the mdast node to render
 * @param {RangedStyles} annotations - the styles to apply to specific ranges in the markdown
 * @param {string} key? - the key to set on the react element
 *
 * @return {JSX.Element}
 */
function visitNode(
	node: Node,
	context: MarkdownContext,
	key: string
): JSX.Element {
	const Component = componentsLookup[node.type] ?? NotAllowed
	return (
		<Component
			key={key}
			context={context}
			// @ts-expect-error we assume that node type maps to the component which takes that node as an argument
			node={node}
		/>
	)
}

/**
 * renderChildren - render the children of a mdast node
 *
 * @param {Array<Node> | null | undefined} children - the children to render
 * @param {RangedStyles} annotations - the annotations to in the markdown
 *
 * @return {Array<JSX.Element> | null}
 */
function renderChildren(
	children: Array<Node> | null | undefined,
	context: MarkdownContext
): Array<JSX.Element> | null {
	if (!children?.length) {
		return null
	}

	const currentComponents = []
	for (let i = 0; i < children.length; i++) {
		currentComponents.push(
			visitNode(
				// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				children[i],
				context,
				String(i)
			)
		)
	}

	return currentComponents
}

/**
 * getHTMLComponentToUse - get the html component to use, takes disallowed elements into account
 *
 * @param {string} wantedTag - the tag that wants to be rendered
 * @param {MarkdownContext} context - the context that this markdown is rendering in
 *
 * @returns {string} - the tag to use
 */
function getHTMLComponentToUse<T extends string>(
	wantedTag: T,
	context: MarkdownContext
): T | 'div' {
	return context.allowedHtmlElements.has(wantedTag) ? wantedTag : 'div'
}

export class MarkdownErrorBoundary extends React.Component<
	{ markdown: string; children: ReactNode },
	{ hasError: boolean }
> {
	constructor(props: { markdown: string; children: ReactNode }) {
		super(props)
		this.state = { hasError: false }
	}

	static getDerivedStateFromError() {
		return { hasError: true }
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		console.error(
			'Error rendering markdown: ',
			error,
			info,
			` when rendering "${this.props.markdown}"`
		)
	}

	render() {
		if (this.state.hasError) {
			return (
				<div className="text-error font-bold">
					An error has occurred, please contact Mission.io support with the
					location of this error.
				</div>
			)
		}

		return this.props.children
	}
}

/**
 * abortSignalAnyPolyfill - a polyfill for `AbortSignal.any`. If any of the given signals abort, then the return signal will abort
 *
 * @param {Array<AbortSignal>} signals - if any of these signals abort, the returned signal will be aborted
 *
 * @return {AbortSignal}
 */
function abortSignalAnyPolyfill(signals: Array<AbortSignal>): AbortSignal {
	const controller = new AbortController()

	signals.forEach(signal =>
		signal.addEventListener('abort', () => {
			controller.abort()
		})
	)

	return controller.signal
}
